import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Users } from 'lucide-react';
import { Button } from './ui/button';
import { HoursModal } from './HoursModal';
import { CookingTimeModal } from './CookingTimeModal';
import { MenuOptionModal } from './MenuOptionModal';
import { WaiterRequestModal } from './WaiterRequestModal';
import { SessionControlBar, SessionProgressContainer } from './session';
import type { SessionProgressContainerHandle } from './session/SessionProgressContainer';
import { useSessionTimer } from '@/hooks/useSessionTimer';
import { useSessionTermination } from '@/hooks/useSessionTermination';
import { useSafeArea } from '@/hooks/useSafeArea';
import { ShoppingCart, Search } from 'lucide-react';

// 분리된 컴포넌트 import
import {
  MenuCategoryTabs,
  MenuItemCard,
  TableOrderHeader,
  PickupStoreInfo,
  StoreSelectionModal,
} from './pickup';

// 분리된 모듈 import
import type { MenuItem, Store, MenuCategoryItem } from '@/data';
import {
  isMenuItemDisabledByDisplayMode,
  isMenuItemHiddenByDisplayMode,
} from '@/types/menuItem';
import { menuTranslations } from '@/i18n';
import { formatCurrency } from '@/utils/currency';
import { usePickupMenuData, createMenuTranslations, createCategoryTranslations } from '@/hooks/usePickupMenuData';
import { getStoreSwitchPath } from '@/lib/app-utils';
import { useOrganizationStores } from '@/hooks/useOrganizationStores';
import type { CartItem, CartItemInput, SelectedOption } from '@/types';
import { useApp } from '@/contexts/AppContext';
import { useCartItems } from '@/contexts/CartContext';
import { navigationEvents } from '@/lib/navigation-events';

/**
 * 픽업 줄서기 게이트(admin#2172/menu#804) — `store.pickup_requires_waitlist=true`
 * 매장에서 called 확인 전(또는 실패)일 때 RestaurantPage 가 전달하는 차단 상태.
 * 존재하면 장바구니 진입 CTA 를 "줄서기 하러 가기" 배너로 대체한다 — 메뉴 열람 자체는
 * 계속 허용한다(view 강등이 아니다).
 */
export interface PickupWaitlistGate {
  title: string;
  description: string;
  ctaLabel: string;
  onGoToWaitlist: () => void;
}

interface CommonPickupMenuProps {
  restaurant: {
    id: string;
    nameKey: string;
    name: string;
    cuisine: string;
    imageUrl: string;
    rating: number;
  };
  onBack: () => void;
  onAddToCart: (item: Omit<CartItem, 'quantity'>, quantity?: number) => void;
  onPaymentClick?: () => void;
  cartItemCount?: number;
  orderedItemCount?: number;
  /** #804 — 픽업 줄서기 게이트 차단 상태. mode==='pickup' 에서만 의미가 있다. */
  pickupWaitlistGate?: PickupWaitlistGate;
}

export type PickupMenuProps =
  | (CommonPickupMenuProps & {
      mode: 'table';
      sessionToken: string | null;
      entryToken?: string;
    })
  | (CommonPickupMenuProps & {
      mode?: 'pickup' | 'view';
      sessionToken?: never;
      entryToken?: never;
    });

export function PickupMenu({
  restaurant,
  onBack: _onBack,
  onAddToCart,
  onPaymentClick,
  mode = 'pickup',
  sessionToken,
  entryToken,
  cartItemCount: _propsCartItemCount = 0, // 사용 안함 - useCartItems에서 가져옴
  orderedItemCount = 0,
  pickupWaitlistGate,
}: PickupMenuProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { locale, currency } = useApp();

  // URL `/restaurant/{id}/table/{tableNumber}` 에서 tableNumber 추출.
  // URL 파생이 단일 진실 원천 (#414 — 과거 currentTable dead state 는 제거됨).
  const tableNumber = useMemo(() => {
    if (mode !== 'table') return null;
    const match = location.pathname.match(/\/table\/([^/]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  }, [mode, location.pathname]);

  // 표시용 테이블 번호/매장명을 후속 페이지(/cart, /checkout)에서도 사용하도록 동기화.
  // trusted table_store_id는 QR persist/검증된 durable restore만 생성한다.
  // 테이블 모드 진입 시 set / 픽업 모드 또는 매장 변경 시 clear.
  useEffect(() => {
    if (mode === 'table' && tableNumber) {
      sessionStorage.setItem('table_number', tableNumber);
      sessionStorage.setItem('table_store_name', restaurant.name);
    } else if (mode !== 'table') {
      sessionStorage.removeItem('table_number');
      sessionStorage.removeItem('table_store_name');
      sessionStorage.removeItem('table_store_id');
    }
  }, [mode, tableNumber, restaurant.name]);
  // 전역 장바구니를 현재 렌더링 중인 매장으로 스코프한다.
  // 다른 매장 URL로 직접 이동해도 기존 매장의 카트 바를 노출하지 않는다.
  const { cartItems } = useCartItems();
  const currentRestaurantId = String(restaurant.id);
  const currentRestaurantCartItems = useMemo(
    () => cartItems.filter((item) => String(item.restaurantId) === currentRestaurantId),
    [cartItems, currentRestaurantId],
  );
  const cartItemCount = useMemo(
    () => currentRestaurantCartItems.reduce((total, item) => total + item.quantity, 0),
    [currentRestaurantCartItems],
  );
  const cartTotal = useMemo(
    () => Math.round(
      currentRestaurantCartItems.reduce((sum, item) => sum + item.price * item.quantity, 0) * 100,
    ) / 100,
    [currentRestaurantCartItems],
  );

  // 세션 타이머는 진입 시 검증된 명시적 스냅샷만 소비한다.
  const sessionTimer = useSessionTimer(sessionToken ?? null);
  useSessionTermination(sessionTimer.terminal);

  // #648(codex Important): iOS 하단 인셋 — 하단 도크가 sticky 로 viewport 바닥에 붙으므로
  // 홈 인디케이터 영역만큼 도크 최하단 요소(장바구니 푸터 또는 진행바 CTA)에 padding 을 더한다.
  const { bottomCSS: safeAreaBottom } = useSafeArea();

  // #230 — 초기값에 mock 카테고리 id ('appetizers') 박제 금지.
  // categories 가 API 응답으로 로드되면 useEffect 가 첫 번째 카테고리로 자동 선택.
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [showStoreModal, setShowStoreModal] = useState(false);
  const [showHoursModal, setShowHoursModal] = useState(false);
  const [showCookingTimeModal, setShowCookingTimeModal] = useState(false);
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const [selectedMenuItem, setSelectedMenuItem] = useState<MenuItem | null>(null);
  const [showMenuOptionModal, setShowMenuOptionModal] = useState(false);
  const [showWaiterModal, setShowWaiterModal] = useState(false);
  // #634: 서버 권위 미결제 후불 존재 — SessionProgressContainer(하단 진행바)의 단일 조회를 상향
  // 보고(onUnpaidPostpayChange)로 공유. orderedItemCount(CheckoutContext orderedItems)는 브라우저
  // 메모리라 새로고침/세션 재진입 시 0 이 되어, 하단 진행바는 "진행 중 N건·결제하기"를 표시하는데
  // 상단 PAY 버튼만 비활성이던 데이터 소스 불일치 모순을 해소한다.
  const [hasServerUnpaidPostpay, setHasServerUnpaidPostpay] = useState(false);
  // 상단 PAY → 하단 진행바 "결제하기"와 동일한 세션 전체결제 플로우([4] ChargeSessionModal) 합류용.
  const sessionChargeRef = useRef<SessionProgressContainerHandle>(null);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // API 메뉴 및 매장 데이터 조회
  const {
    menuItems,
    categories,
    loading: menuLoading,
    error: menuError,
    isApiData: _isApiData,
    storeName: _apiStoreName,
    storeInfo,
    organizationId,
    brandId,
    storeApiId,
  } = usePickupMenuData(restaurant.id);

  // 동일 매장 그룹 우선, 그룹 미지정 시 현재 매장만 조회
  const { stores: orgStores, loading: _orgStoresLoading } = useOrganizationStores(organizationId, brandId, storeApiId);

  // 매장 선택 모달에 표시할 매장 목록 (현재 매장이 없으면 추가)
  const availableStores = useMemo(() => {
    if (!storeInfo) return orgStores;

    // 현재 매장이 목록에 있는지 확인
    const currentStoreExists = orgStores.some(s => s.id === storeInfo.id);
    if (currentStoreExists) return orgStores;

    // 현재 매장이 없으면 맨 앞에 추가
    return [storeInfo, ...orgStores];
  }, [orgStores, storeInfo]);

  // storeInfo가 로드되면 selectedStore 설정
  useEffect(() => {
    if (storeInfo && !selectedStore) {
      setSelectedStore(storeInfo);
    }
  }, [storeInfo, selectedStore]);

  // #514 — 매장 선택 모달에서 다른 매장 선택 시 URL 변경 (getStoreSwitchPath 헬퍼 위임).
  // 박제 [feedback_silent_pass_recurrence] 정합 — table 모드 매칭 실패 시 state/URL 모두 차단.
  // CodeRabbit Major — null 가드를 setState 앞으로 이동 (URL 미변경 + state 만 변경되는 silent shift 차단).
  const handleStoreSwitch = useCallback((store: Store) => {
    // #541: view 모드는 pickup 처럼 처리 (테이블 번호 불필요)
    const switchMode: 'pickup' | 'table' | undefined = mode === 'view' ? 'pickup' : mode;
    const targetPath = getStoreSwitchPath(switchMode, location.pathname, store.id);
    if (targetPath === null) {
      console.warn('[#514] table 모드 진입 중인데 URL 에 테이블 번호 매칭 실패 — state/URL 모두 보류', location.pathname);
      return;
    }
    setSelectedStore(store);
    setShowStoreModal(false);
    navigate(targetPath);
  }, [mode, navigate, location.pathname]);

  // 첫 카테고리 자동 선택
  useEffect(() => {
    if (categories.length > 0 && !categories.find(c => c.id === selectedCategory)) {
      setSelectedCategory(categories[0].id);
    }
  }, [categories, selectedCategory]);

  const t = menuTranslations[locale];

  // 통화 포맷팅 함수
  const formatPrice = useCallback((price: number): string => {
    return formatCurrency(price, currency);
  }, [currency]);

  // API 메뉴용 동적 번역 생성 (향후 API 다국어 지원 시 사용 예정)
  const _apiMenuTranslations = useMemo(() => createMenuTranslations(menuItems), [menuItems]);
  const _apiCategoryTranslations = useMemo(() => createCategoryTranslations(categories), [categories]);

  // 메뉴 이름/설명 가져오기 헬퍼
  const getMenuName = (item: MenuItem): string => {
    if (item._name) return item._name;
    const translation = t.menuItems[item.nameKey as keyof typeof t.menuItems];
    return translation?.name || item.nameKey;
  };

  const getMenuDescription = (item: MenuItem): string => {
    if (item._description) return item._description;
    const translation = t.menuItems[item.descriptionKey as keyof typeof t.menuItems];
    return translation?.description || '';
  };

  const getCategoryName = (cat: MenuCategoryItem): string => {
    // #230 — API 카테고리는 _name (transformApiCategory) 우선 사용.
    // i18n key (mock 카테고리) 인 경우만 번역. 미매칭 시 raw key 노출 차단 — 빈 문자열 반환.
    if (cat._name) return cat._name;
    return t.categories[cat.nameKey as keyof typeof t.categories] ?? '';
  };

  // Hours modal 이벤트 리스너
  useEffect(() => {
    const handleOpenHoursModal = (event: CustomEvent) => {
      if (event.detail?.storeId) {
        const store = availableStores.find((s) => s.id === event.detail.storeId);
        if (store) {
          setSelectedStore(store);
          setShowHoursModal(true);
        }
      }
    };

    window.addEventListener('openHoursModal', handleOpenHoursModal as EventListener);

    return () => {
      window.removeEventListener('openHoursModal', handleOpenHoursModal as EventListener);
    };
  }, [availableStores]);

  // #1439 G4 — display_state='hidden' 메뉴는 영업시간 외 자동 미노출 (BE 가 판정).
  const visibleMenuItems = useMemo(
    () => menuItems.filter((item) => !isMenuItemHiddenByDisplayMode(item)),
    [menuItems]
  );

  // 검색어에 따라 메뉴 필터링
  const filteredMenuItems = searchQuery.trim()
    ? visibleMenuItems.filter((item) => {
        const itemName = getMenuName(item).toLowerCase();
        const itemDescription = getMenuDescription(item).toLowerCase();
        const query = searchQuery.toLowerCase();
        return itemName.includes(query) || itemDescription.includes(query);
      })
    : visibleMenuItems;

  // 카테고리별 메뉴 그룹화. #230 AD1 — 'all' 가상 카테고리는 모든 메뉴 포함.
  const groupedMenuItems = categories.reduce(
    (acc, category) => {
      acc[category.id] = category.id === 'all'
        ? filteredMenuItems
        : filteredMenuItems.filter((item) => item.category === category.id);
      return acc;
    },
    {} as Record<string, MenuItem[]>
  );

  const handleMenuItemClick = (item: MenuItem) => {
    // #1439 G4 — display_state='disabled' 메뉴는 영업시간 외 주문 차단.
    if (isMenuItemDisabledByDisplayMode(item)) {
      return;
    }
    setSelectedMenuItem(item);
    setShowMenuOptionModal(true);
  };

  // MenuOptionModal의 onAddToCart 시그니처에 맞는 wrapper 함수
  const handleModalAddToCart = (item: CartItemInput, quantity: number, _selectedOptions?: SelectedOption[]) => {
    // CartItemInput을 Omit<CartItem, 'quantity'>로 변환
    const cartItem: Omit<CartItem, 'quantity'> = {
      id: item.id,
      menuItemId: item.id,
      name: item.name || item.nameKey || '',
      description: item.description || item.descriptionKey || '',
      price: item.price,
      originalPrice: item.originalPrice,
      imageUrl: item.imageUrl,
      category: item.category || '',
      // transformApiMenuToMenuItem에서 restaurantId가 항상 매핑됨을 보장
      restaurantId: item.restaurantId!,  // ✅ Type assertion (always exists)
      restaurantName: item.restaurantName || restaurant.name,
      customizations: item.customizations,
      selectedOptions: item.selectedOptions,
      // #498: 입력형 옵션 사용자 입력값 cart 보존 (BE 전송 흐름 정합).
      inputValues: item.inputValues,
      // #126: 옵션 편집용 원본 데이터 저장 (장바구니 변경 버튼에서 사용)
      _originalOptions: item._originalOptions,
      // #280 보강: 가용성 정보도 같이 보존 — 편집 모달에서 판매 시간 행 노출용
      _availability: item._availability,
    };
    onAddToCart(cartItem, quantity);
  };

  const handleHoursModalOpen = () => {
    if (!selectedStore) return;
    const event = new CustomEvent('openHoursModal', {
      detail: { storeId: selectedStore.id },
    });
    window.dispatchEvent(event);
  };

  // 테이블 모드 활성 조건 (no-op 클릭 불가 보장):
  //  - 서버 미결제 후불 존재 → 항상 활성. 클릭은 onPaymentClick 유무와 무관하게 세션 결제
  //    플로우(ref.openCharge)로 처리되므로 직접 진입(/restaurant/:id/table/:n — onPaymentClick
  //    미전달) 경로에서도 동작한다. (#635 의 Boolean(onPaymentClick) 전제 게이트는 이 경로에서
  //    버튼을 계속 죽여 사용자 보고 결함이 잔존 — openCharge 브리지로 대체)
  //  - 로컬 orderedItems 만 존재 → 레거시 체크아웃 위임이라 onPaymentClick 있을 때만 활성.
  //
  // #654 codex I3 R2: orderedItemCount OR 분기는 SSOT(hasPayableSessionLines) 우회가 아니다.
  //  - canPay 는 **표시(버튼 활성)** 게이트일 뿐, 세션 결제 **진입**은 아래 handlePaymentClick 의
  //    openCharge 단일 경로이고 그 진입은 이중 SSOT 게이트를 통과한다:
  //    ① hasServerUnpaidPostpay 자체가 SessionProgressContainer 의
  //       `!isLoading && hasPayableSessionLines(data)` 상향 보고(빈 items 계약 위반 → false)
  //    ② openCharge() imperative 핸들이 호출 시점 data 로
  //       `if (isLoading || !hasPayableSessionLines(data)) return` 재검사(stale 보고 race 차단)
  //  - OR 분기(orderedItemCount>0 && onPaymentClick)의 클릭은 hasServerUnpaidPostpay=false 인
  //    경우에만 도달하며 onPaymentClick(레거시 신규주문 체크아웃)으로만 위임 — 세션 결제
  //    (openCharge)에는 절대 도달하지 않는다. 실증: PickupMenu.view.test "빈 items" 케이스 +
  //    SessionProgressContainer.headerBridge.test "빈 items → openCharge no-op".
  const canPay = mode === 'table'
    ? hasServerUnpaidPostpay || (orderedItemCount > 0 && Boolean(onPaymentClick))
    : cartItemCount > 0;

  const handlePaymentClick = () => {
    if (!canPay) return;
    // 서버 미결제 후불 존재 → 하단 진행바 "결제하기"와 동일한 [4] ChargeSessionModal 직행.
    // 두 버튼이 서로 다른 결제 플로우를 열면 안 된다 (같은 미결제 주문서 = 같은 모달).
    // #654 I3: openCharge 는 컨테이너 내부에서 SSOT(hasPayableSessionLines) 재검사 후 연다.
    if (mode === 'table' && hasServerUnpaidPostpay) {
      sessionChargeRef.current?.openCharge();
      return;
    }
    // 레거시: 로컬 orderedItems 기반 체크아웃 위임 (App 라우트 — onPaymentClick=handleCheckout).
    // 세션 결제 진입 아님 — 신규 주문 체크아웃 플로우 (#654 I3 주석 참조).
    onPaymentClick?.();
  };

  const getNoResultsText = () => {
    switch (locale) {
      case 'ko':
        return '검색 결과가 없습니다';
      case 'es':
        return 'No se encontraron resultados';
      default:
        return 'No results found';
    }
  };

  const getResultsCountText = (count: number) => {
    switch (locale) {
      case 'ko':
        return `${count}개 결과`;
      case 'es':
        return `${count} resultados`;
      default:
        return `${count} results`;
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-muted">
      {/* 레스토랑 헤더 */}
      <div className="bg-background p-[clamp(12px,4vw,24px)] border-b border-border">
        {mode === 'table' ? (
          <TableOrderHeader
            onWaiterClick={() => setShowWaiterModal(true)}
            canCallWaiter={sessionToken !== null && sessionToken !== undefined}
            onPaymentClick={handlePaymentClick}
            canPay={canPay}
            storeName={restaurant.name}
            tableNumber={tableNumber}
            sessionControl={sessionToken ? (
              <SessionControlBar
                sessionTimer={sessionTimer}
              />
            ) : undefined}
          />
        ) : selectedStore ? (
          <PickupStoreInfo
            selectedStore={selectedStore}
            onStoreModalOpen={() => setShowStoreModal(true)}
            onHoursModalOpen={handleHoursModalOpen}
            onCookingTimeModalOpen={() => setShowCookingTimeModal(true)}
            translations={{
              storeLocationSelect: t.storeLocationSelect,
              storeNames: t.storeNames,
              storeStatus: t.storeStatus,
            }}
          />
        ) : menuLoading ? (
          /* 로딩 상태 */
          <div className="animate-pulse flex gap-4 items-start">
            <div className="w-[clamp(100px,26vw,150px)] h-[clamp(100px,26vw,150px)] bg-gray-200 dark:bg-gray-700 rounded-lg" />
            <div className="flex-1 space-y-3">
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3" />
            </div>
          </div>
        ) : (
          /* 에러 상태: 매장을 찾을 수 없음 (#61, #62) */
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="text-[clamp(14px,3.5vw,18px)] font-medium text-muted-foreground mb-2">
              {locale ==='ko' ? '매장을 찾을 수 없습니다' :
               locale ==='es' ? 'No se encontró la tienda' :
               'Store not found'}
            </div>
            <div className="text-[clamp(12px,3vw,14px)] text-muted-foreground">
              {menuError?.message || (locale ==='ko' ? '요청한 매장 정보를 불러올 수 없습니다.' :
               locale ==='es' ? 'No se pudo cargar la información de la tienda solicitada.' :
               'Could not load the requested store information.')}
            </div>
          </div>
        )}
      </div>

      {/* 카테고리 탭 */}
      <MenuCategoryTabs
        categories={categories}
        selectedCategory={selectedCategory}
        onCategorySelect={setSelectedCategory}
        isSearchMode={isSearchMode}
        onSearchModeToggle={setIsSearchMode}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        translations={{ categories: t.categories }}
      />

      {/* 메뉴 아이템 목록 */}
      <div className="flex-1 p-[min(4vw,1rem)] overflow-x-hidden font-['Noto_Sans',sans-serif] mt-[0px] mr-[0px] mb-[40px] ml-[0px]">
        {/* 로딩 중 */}
        {menuLoading ? (
          <div className="flex flex-col items-center justify-center py-[min(12vw,3rem)]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4" />
            <p className="text-[clamp(12px,3vw,16px)] text-muted-foreground">
              {locale ==='ko' ? '메뉴를 불러오는 중...' :
               locale ==='es' ? 'Cargando menú...' :
               'Loading menu...'}
            </p>
          </div>
        ) : !menuLoading && menuItems.length === 0 ? (
          /* 메뉴 없음 */
          <div className="flex flex-col items-center justify-center py-[min(12vw,3rem)]">
            <div className="text-[clamp(40px,10vw,60px)] mb-4">🍽️</div>
            <p className="text-[clamp(14px,3.5vw,18px)] font-medium text-muted-foreground text-center">
              {locale ==='ko' ? '등록된 메뉴가 없습니다' :
               locale ==='es' ? 'No hay menú disponible' :
               'No menu available'}
            </p>
            <p className="text-[clamp(12px,3vw,14px)] text-muted-foreground text-center mt-2">
              {locale ==='ko' ? '매장에서 메뉴를 준비 중입니다.' :
               locale ==='es' ? 'La tienda está preparando el menú.' :
               'The store is preparing the menu.'}
            </p>
          </div>
        ) : isSearchMode && searchQuery.trim() && filteredMenuItems.length === 0 ? (
          /* 검색 결과 없음 */
          <div className="flex flex-col items-center justify-center py-[min(12vw,3rem)]">
            <Search className="w-[min(16vw,4rem)] h-[min(16vw,4rem)] text-muted-foreground mb-[min(4vw,1rem)]" />
            <p className="text-[clamp(12px,3vw,16px)] text-muted-foreground text-center">
              {getNoResultsText()}
            </p>
            <p className="text-[clamp(10px,2.5vw,14px)] text-muted-foreground text-center mt-[min(2vw,0.5rem)]">
              "{searchQuery}"
            </p>
          </div>
        ) : isSearchMode ? (
          /* 검색 결과 */
          <div>
            <div className="mb-[clamp(8px,2vw,12px)] ml-[clamp(8px,2vw,16px)]">
              <p className="text-[clamp(11px,2.8vw,14px)] text-muted-foreground">
                {getResultsCountText(filteredMenuItems.length)}
              </p>
            </div>
            <div className="space-y-[min(3vw,0.75rem)]">
              {filteredMenuItems.map((item) => (
                <MenuItemCard
                  key={item.id}
                  item={item}
                  translations={{
                    menuItems: t.menuItems,
                    discount: t.discount,
                    shortDays: t.shortDays,
                  }}
                  formatPrice={formatPrice}
                  onClick={() => handleMenuItemClick(item)}
                  storeTimezone={storeInfo?.timezone}
                />
              ))}
            </div>
          </div>
        ) : (
          /* 카테고리별 메뉴 */
          categories.map((category) => {
            const categoryItems: MenuItem[] = groupedMenuItems[category.id] || [];
            if (categoryItems.length === 0) return null;

            return (
              <div key={category.id} id={`category-${category.id}`} className="mb-[5px]">
                <div className="mt-[10px] mb-[5px]">
                  <h2 className="text-[clamp(12px,2.3vw,18px)] font-bold text-foreground font-['Noto_Sans',sans-serif] mb-[1px] mt-[1px] first:mt-0 leading-tight tracking-tight ml-[clamp(8px,2vw,16px)]">
                    {getCategoryName(category)}
                  </h2>
                </div>

                <div className="space-y-[min(3vw,0.75rem)]">
                  {categoryItems.map((item) => (
                    <MenuItemCard
                      key={item.id}
                      item={item}
                      translations={{
                        menuItems: t.menuItems,
                        discount: t.discount,
                        shortDays: t.shortDays,
                      }}
                      formatPrice={formatPrice}
                      onClick={() => handleMenuItemClick(item)}
                      storeTimezone={storeInfo?.timezone}
                    />
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* 하단 도크 — 세션 진행바 + 장바구니 푸터를 viewport 바닥에 sticky 고정.
          - 메뉴가 길어도 스크롤 없이 결제/주문 CTA 가 보이도록 sticky bottom-0 (일반 flow 유지라
            #634 가 우려한 fixed 겹침 없음). 두 바를 한 래퍼로 묶어 2단 스택 시 상호 겹침 방지.
          - z-index 미부여(z-auto) — #634 z 정책: 바는 CartModal(50)/시트(55)/결제모달(58) 등
            모든 오버레이 아래에 머문다. sticky 는 stacking context 를 생성하므로 내부 모달은
            SessionProgressContainer 가 document.body 로 portal 해 z 계층(#634)을 보존한다.
          - 전역 TabNav(fixed z-50)와는 상호배제 렌더 — route 기반 메뉴 페이지(/restaurant/*)에서
            BottomNavigation 이 TabNav 를 미렌더해 도크를 덮지 않는다(#648 codex Critical). */}
      <div data-testid="pickup-bottom-dock" className="sticky bottom-0">
        {/* #603 [2][3]: 세션 진행 현황 바 + 주문 시트 + 전체결제 — 테이블 모드 한정.
            미결제 후불 주문서(append=Open 1건) 존재 시에만 내부적으로 렌더(없으면 null).
            장바구니에 담는 중이면 슬림 행만(아래 장바구니 바와 2단 스택), 비어있으면 결제 CTA 확장. */}
        <SessionProgressContainer
          ref={sessionChargeRef}
          enabled={mode === 'table'}
          cartHasItems={cartItemCount > 0}
          tableNumber={tableNumber}
          sessionToken={sessionToken ?? null}
          entryToken={entryToken ?? null}
          onUnpaidPostpayChange={setHasServerUnpaidPostpay}
        />

        {/* #804: 픽업 줄서기 게이트 차단 — 장바구니 CTA 대신 "줄서기 하러 가기" 배너.
            메뉴 열람/장바구니 담기는 그대로 허용하고, 결제 진입 동선만 대체한다. */}
        {mode === 'pickup' && pickupWaitlistGate ? (
          <div
            data-testid="pickup-waitlist-gate-banner"
            className="bg-background border-t border-border p-[min(4vw,1rem)]"
            style={{ paddingBottom: `calc(min(4vw,1rem) + ${safeAreaBottom})` }}
          >
            <div className="mb-2 flex items-start gap-2">
              <Users className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <div>
                <p className="text-[calc(0.85rem+0.1vw)] font-medium text-foreground">
                  {pickupWaitlistGate.title}
                </p>
                <p className="text-[calc(0.75rem+0.1vw)] text-muted-foreground">
                  {pickupWaitlistGate.description}
                </p>
              </div>
            </div>
            <Button
              onClick={pickupWaitlistGate.onGoToWaitlist}
              className="w-full bg-primary hover:bg-primary-hover text-white py-4 text-[calc(1rem+0.25vw)]"
            >
              {pickupWaitlistGate.ctaLabel}
            </Button>
          </div>
        ) : (
          /* 장바구니 푸터 — #541: view 모드에서 미렌더.
             도크 최하단 요소이므로 iOS safe-area 하단 인셋을 기존 padding 에 가산(#648). */
          mode !== 'view' && cartItemCount > 0 && (
            <div
              data-testid="pickup-cart-nav"
              className="bg-background border-t border-border p-[min(4vw,1rem)]"
              style={{ paddingBottom: `calc(min(4vw,1rem) + ${safeAreaBottom})` }}
            >
              <Button
                onClick={navigationEvents.toggleCart}
                aria-label={`${t.viewCart}: ${cartItemCount} ${t.items}, ${formatPrice(cartTotal)}`}
                className="w-full bg-primary hover:bg-primary-hover text-white flex items-center justify-between py-4 text-[calc(1rem+0.25vw)]"
              >
                <div className="flex items-center gap-2">
                  <ShoppingCart className="h-5 w-5" />
                  <span>{t.viewCart}</span>
                </div>
                <div className="flex items-center gap-4">
                  <span>
                    {cartItemCount} {t.items}
                  </span>
                  <span>{formatPrice(cartTotal)}</span>
                </div>
              </Button>
            </div>
          )
        )}
      </div>

      {/* 매장 선택 모달 */}
      {selectedStore && (
        <StoreSelectionModal
          isOpen={showStoreModal}
          onClose={() => setShowStoreModal(false)}
          stores={availableStores}
          selectedStore={selectedStore}
          onStoreSelect={handleStoreSwitch}
          translations={{
            storeSelection: t.storeSelection,
            storeNames: t.storeNames,
            storeStatus: t.storeStatus,
            cancel: t.cancel,
          }}
        />
      )}

      {/* 영업시간 모달 — Q7 박제 옵션 C (menu#347) BE 4 필드 전달 */}
      {selectedStore && storeApiId && (
        <HoursModal
          isOpen={showHoursModal}
          onClose={() => setShowHoursModal(false)}
          storeId={storeApiId}
          storeName={selectedStore.name}
          storeAddress={selectedStore.address}
          storeStatus={selectedStore.apiStatus}
          storeIsOpenNow={selectedStore.isOpenNow}
          storeClosureReason={selectedStore.closureReason}
          storeNextAvailableAt={selectedStore.nextAvailableAt}
          storeTimezone={selectedStore.timezone}
          storeHasQ7Hours={selectedStore.hasQ7Hours}
        />
      )}

      {/* 조리시간 모달 */}
      <CookingTimeModal
        isOpen={showCookingTimeModal}
        onClose={() => setShowCookingTimeModal(false)}
        storeName={restaurant.name}
      />

      {/* 메뉴 옵션 모달 — #541: view 모드에서 담기 버튼 미렌더 */}
      <MenuOptionModal
        item={selectedMenuItem}
        isOpen={showMenuOptionModal}
        onClose={() => {
          setShowMenuOptionModal(false);
          setSelectedMenuItem(null);
        }}
        onAddToCart={handleModalAddToCart}
        apiOptions={selectedMenuItem?._options}
        viewOnly={mode === 'view'}
      />

      {/* 웨이터 호출 모달 — #451: 테이블 모드 진입 시 URL 파생 tableNumber 자동 주입.
          픽업 모드(tableNumber === null)는 prop 미전달 → 모달 내부 입력 필드 노출 (손님 직접 입력). */}
      {storeApiId && (
        <WaiterRequestModal
          isOpen={showWaiterModal}
          onClose={() => setShowWaiterModal(false)}
          restaurantName={restaurant.name}
          storeId={storeApiId}
          tableNumber={tableNumber ?? undefined}
        />
      )}
    </div>
  );
}
